export const speakerRentalPage = {
  site: 'miamisoundrental',
  path: '/speaker-rental',
  aliases: [],
  canonicalUrl: 'https://miamisoundrental.com/speaker-rental',
  locale: 'en-US',
  location: 'miami',
  routeType: 'static',
  templateKey: 'speaker-rental-v1',
  indexPolicy: 'index'
}

export const speakerRentalRevision = {
  schemaVersion: 1,
  seo: {
    title: 'Speaker Rental from $60/day in Miami & Fort Lauderdale',
    description: 'Compare speaker rentals in Miami and Fort Lauderdale from $60/24h. Check live availability, pickup, delivery and setup, then reserve online.',
    canonicalUrl: 'https://miamisoundrental.com/speaker-rental',
    robots: {
      index: true,
      follow: true
    },
    openGraph: {
      type: 'website',
      url: 'https://miamisoundrental.com/speaker-rental',
      title: 'Speaker Rental from $60/day in Miami & Fort Lauderdale',
      description: 'Compare speaker rentals in Miami and Fort Lauderdale from $60/24h. Check live availability, pickup, delivery and setup, then reserve online.',
      images: [{
        url: 'https://miamisoundrental.com/Pictures/categories-v2/orlando-speaker-rental-hero-v2.jpg',
        width: 1672,
        height: 960,
        alt: 'Powered speakers supporting a professional event presentation'
      }]
    },
    twitter: {
      title: 'Speaker Rental from $60/day in Miami & Fort Lauderdale',
      description: 'Compare speaker rentals in Miami and Fort Lauderdale from $60/24h. Check live availability, pickup, delivery and setup, then reserve online.',
      cardType: 'summary_large_image',
      images: [{
        url: 'https://miamisoundrental.com/Pictures/categories-v2/orlando-speaker-rental-hero-v2.jpg',
        width: 1672,
        height: 960,
        alt: 'Powered speakers supporting a professional event presentation'
      }]
    },
    keywords: [
      'speaker rental',
      'speaker rental Miami',
      'speaker rental Fort Lauderdale',
      'party speaker rental',
      'powered speaker rental',
      'portable speaker rental',
      'outdoor speaker rental',
      'JBL speaker rental'
    ],
    primaryKeyword: 'speaker rental Miami',
    secondaryKeywords: [
      'speaker rental Fort Lauderdale',
      'party speaker rental',
      'powered speaker rental',
      'portable speaker rental',
      'outdoor speaker rental'
    ],
    searchIntent: 'transactional',
    entities: [
      'Miami Sound Rental',
      'powered speakers',
      'portable speakers',
      'speaker pairs',
      'JBL speakers',
      'loudspeakers'
    ],
    targetLocations: [
      'Miami',
      'Fort Lauderdale',
      'South Florida'
    ]
  },
  content: {
    h1: 'Speaker Rental',
    custom: {
      introductionImage: '/Pictures/categories-v2/speaker-rental-introduction-v2.webp',
      introductionImageAlt: 'DJ setup flanked by professional powered speakers and subwoofers',
      heroImagePosition: 'center'
    },
    hero: {
      title: 'Speaker Rental',
      paragraph: 'Compare powered speakers, speaker pairs, and straightforward party bundles with live availability and pricing. Choose pickup or professional delivery and setup.',
      picture: '/Pictures/categories-v2/speaker-rental-hero-v2.jpg',
      imageAlt: 'Professional speakers installed for an event venue'
    },
    intro: 'Find and rent the right powered speaker or straightforward speaker bundle for an event in South Florida.',
    breadcrumbs: [
      {
        name: 'Home',
        url: 'https://miamisoundrental.com'
      },
      {
        name: 'Sound System Rental',
        url: 'https://miamisoundrental.com/sound-system-rental'
      },
      {
        name: 'Speaker Rental',
        url: 'https://miamisoundrental.com/speaker-rental'
      }
    ],
    internalLinks: [
      {
        label: 'Speaker and Microphone',
        url: '/sound/microphone-and-speaker',
        relationship: 'related'
      },
      {
        label: 'Complete Sound System Rental',
        url: '/sound-system-rental',
        relationship: 'parent'
      },
      {
        label: 'Microphone Rental',
        url: '/microphone-rental',
        relationship: 'related'
      },
      {
        label: 'Karaoke Rental',
        url: '/karaoke-rental',
        relationship: 'related'
      },
      {
        label: 'Subwoofers',
        url: '/sound/subwoofer',
        relationship: 'child'
      },
      {
        label: 'Monitors',
        url: '/sound/monitors',
        relationship: 'child'
      }
    ],
    sections: [
      {
        key: 'rental-intro',
        type: 'rich_text',
        heading: 'Rent the Right Speakers for Your Event',
        body: '<p><strong>Compare individual speaker rentals with live availability and clear pricing.</strong> Choose powered speakers or speaker pairs for meetings, ceremonies, parties, DJ sets, presentations, and other South Florida events.</p><p>Select your dates to compare pickup, delivery, and setup options. If the event needs subwoofers, several listening areas, multiple microphones, or technical operation, our audio team can recommend a configuration or help you move to a <a href="/sound-system-rental">complete sound system rental</a>.</p>'
      },
      {
        key: 'selection-help',
        type: 'rich_text',
        heading: 'Choose the Right Speaker Rental',
        subheading: "Match the speaker to the audience, room, program, and audio source—not wattage alone.",
        body: '<p>For speech and presentations, one or two powered speakers may be enough. Music-heavy events and DJ sets often benefit from a matched subwoofer, while outdoor spaces and multiple listening zones usually require a more carefully planned PA system.</p><p>Compare <strong>event size</strong>, <strong>speaker type</strong>, <strong>sound quality</strong>, and <strong>price</strong>. Then select <strong>&quot;Choose Dates &amp; Rent Online&quot;</strong> to see availability and choose warehouse pickup or professional delivery and setup.</p>'
      },
      {
        key: 'speaker-guide',
        type: 'rich_text',
        heading: 'How Many Speakers Do You Need?',
        body: '<p>For speech, ceremonies, and background music in one listening area, one or two powered speakers are often the simplest choice. A matched pair provides more even coverage and keeps volume comfortable across the room.</p><p>Audience size is only one factor. Room shape, ceiling height, outdoor conditions, music level, and the number of listening areas all affect coverage. If the event needs more than two speakers, several audio zones, live performers, or continuous technical operation, request a <a href="/sound-system-rental">complete sound-system recommendation</a> instead of guessing from wattage alone.</p>'
      },
      {
        key: 'connectivity',
        type: 'rich_text',
        heading: 'Phone, Laptop, DJ Controller, and Microphone Connections',
        body: '<p>Professional powered speakers commonly accept balanced XLR or TRS signals. A phone or laptop may need the correct cable, a professional Bluetooth adapter, or a small mixer. A wired connection is the most reliable choice for presentations and important program audio.</p><p>Tell us which source you plan to use before checkout so the required signal and power cables can be confirmed. For a ready-made speech package, compare <a href="/sound/microphone-and-speaker">microphone-and-speaker bundles</a>. For microphones by themselves, use the <a href="/microphone-rental">microphone rental page</a>.</p>'
      },
      {
        key: 'use-case-speech',
        type: 'rich_text',
        heading: 'Meetings, Ceremonies, and Speeches',
        subheading: 'Prioritize vocal clarity and even coverage.',
        body: '<p>One powered speaker may suit a small room; a matched pair is usually better for a wider audience. Add a small mixer or choose a <a href="/sound/microphone-and-speaker">microphone-and-speaker bundle</a> when several audio inputs are required.</p>'
      },
      {
        key: 'use-case-party',
        type: 'rich_text',
        heading: 'Parties, Weddings, and DJ Sets',
        subheading: 'Choose enough full-range output for music, then add bass only when the program needs it.',
        body: '<p>Start with one or two powered speakers for straightforward music playback. Dance music and DJ programs may benefit from a matched <a href="/sound/subwoofer">subwoofer</a> or a simple speaker-and-bass bundle.</p>'
      },
      {
        key: 'use-case-outdoor',
        type: 'rich_text',
        heading: 'Outdoor Events and Multiple Listening Areas',
        subheading: 'Coverage and safe placement matter more than a single wattage number.',
        body: '<p>Outdoor spaces, long rooms, and separate audience zones often need distributed speakers and a planned signal path. Use the <a href="/sound-system-rental">complete sound-system page</a> when the setup needs several speakers, crew, or on-site operation.</p>'
      },
      {
        key: 'subwoofers',
        type: 'rich_text',
        heading: 'Subwoofers',
        subheading: 'Add low-frequency support for dance music, DJs, and bass-heavy programs.',
        body: '<p>A subwoofer handles the lowest frequencies so the main speakers can reproduce vocals and instruments more cleanly. It is usually unnecessary for speech-only events. Compare individual models and availability on the <a href="/sound/subwoofer">Subwoofer Rental</a> page.</p>'
      },
      {
        key: 'loudspeakers',
        type: 'rich_text',
        heading: 'Powered Loudspeakers',
        subheading: 'The straightforward choice for speech, music playback, parties, and presentations.',
        body: '<p>Powered loudspeakers contain their own amplification, so they can be used without a separate amplifier rack. Compare output, coverage, sound quality, connection options, pickup eligibility, and price in the catalog rather than choosing by wattage alone.</p>'
      },
      {
        key: 'monitors',
        type: 'rich_text',
        heading: 'Stage Monitors',
        subheading: 'Give performers a dedicated reference mix without redirecting the main speakers toward the stage.',
        body: '<p>Stage monitors are positioned for performers rather than the audience. They are useful for bands, DJs, presenters, and production teams that need to hear program audio clearly. Compare wedges and other monitoring options on the <a href="/sound/monitors">Monitor Rental</a> page.</p>'
      }
    ]
  },
  changeSummary: 'Aligned Speaker Rental with individual-speaker and straightforward-bundle search intent.',
  changeReason: 'The page mixed individual speakers with complete PA, microphone bundles, subwoofers, and line arrays, weakening its approved job and keyword ownership.',
  hypothesis: 'A focused speaker catalog, clearer selection guidance, and deliberate specialist routing will improve relevant organic clicks and rental starts while reducing Audio-family cannibalization.',
  primaryKpi: 'speaker_rental_starts'
}

export const speakerRentalFallback = {
  page: speakerRentalPage,
  revision: speakerRentalRevision,
  source: 'source-fallback'
}
